/**
 * „Rechnung senden“ von Hand (Einheit E, Antoine 26.10.2026): reine Regeln dafuer, WANN die Oberflaeche den Knopf
 * zeigt, WAS statt seiner steht und WARUM eine Rechnung offen ist. Keine Netzaufrufe, keine Betraege.
 */
import type { Payment } from "../ui/types";
import { rechnungGrundText } from "./activity-labels";

const TAG = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });

/** „25.10.2026“ aus einem ISO-Zeitpunkt; ungueltig = null. */
export function rechnungDatum(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : TAG.format(d);
}

/**
 * Darf der Knopf „Rechnung senden“ erscheinen? Bezahlt MIT Zahlungsdatum (ohne Datum sagt der Server „Zahlungsdatum
 * fehlt“, ein Knopf waere eine Sackgasse), die Rechnung ist noch nicht raus und kommt nicht erst mit der Abrechnung;
 * eine stornierte Rechnung hat keinen Knopf. Gilt fuer ALLE Zahlungen, auch alte (Einheit E, Antoine 26.10.2026).
 */
export function rechnungSendbar(p: Pick<Payment, "status" | "paidAt" | "rechnung">): boolean {
  return p.status === "succeeded" && !!p.paidAt && (p.rechnung?.modus === "neu" || p.rechnung?.modus === "vorhandene_senden");
}

interface VerlaufEintrag {
  action: string;
  details: Record<string, unknown> | null;
  createdAt: string;
}

const hatPanne = (paymentId: string, verlauf: readonly VerlaufEintrag[]) =>
  verlauf.some((e) => (e.action === "rechnung.nicht_erstellt" || e.action === "rechnung.versand_fehlgeschlagen") && e.details?.["payment_id"] === paymentId);

/**
 * Soll das Banner „Rechnung offen“ erscheinen? Nur wenn die Rechnung sendbar ist UND entweder der Verlauf zeigt, dass
 * MAESTRO es versucht hat (nicht erstellt / Versand fehlgeschlagen) oder die Zahlung am/nach dem Stichtag des
 * Sofortversands bezahlt wurde. Alte oder migrierte Zahlungen sind evtl. ausserhalb von MAESTRO abgerechnet (Altsystem,
 * LexOffice direkt): ein Banner dort lud zum Klick auf eine zweite echte Rechnung ein. Ohne Stichtag: kein Banner.
 */
export function rechnungOffen(p: Pick<Payment, "id" | "status" | "paidAt" | "rechnung">, verlauf: readonly VerlaufEintrag[] = []): boolean {
  return rechnungSendbar(p) && (p.rechnung?.nachStichtag === true || hatPanne(p.id, verlauf));
}

/** „Rechnung gesendet am 25.10.2026“ - nur bei versendeter Rechnung mit Datum, sonst null. */
export function rechnungGesendetText(p: Pick<Payment, "status" | "rechnung">): string | null {
  if (p.status !== "succeeded" || p.rechnung?.modus !== "bereits_gesendet") return null;
  const am = rechnungDatum(p.rechnung.gesendetAm);
  return am ? `Rechnung gesendet am ${am}` : "Rechnung gesendet";
}

/**
 * Warum die Rechnung zu dieser Zahlung noch offen ist: der JUENGSTE Verlaufseintrag `rechnung.nicht_erstellt`
 * (Grund aus der Liste) bzw. `rechnung.versand_fehlgeschlagen` (Grund im Klartext) zu genau dieser Zahlung. Ohne
 * Eintrag je nach Lage ein ruhiger Satz.
 */
export function rechnungOffenGrund(p: Pick<Payment, "id" | "rechnung">, verlauf: readonly VerlaufEintrag[]): string {
  const passend = verlauf
    .filter((e) => (e.action === "rechnung.nicht_erstellt" || e.action === "rechnung.versand_fehlgeschlagen") && e.details?.["payment_id"] === p.id)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  if (passend?.action === "rechnung.nicht_erstellt") return rechnungGrundText(passend.details?.["grund"]) ?? "Grund nicht näher bekannt";
  if (passend?.action === "rechnung.versand_fehlgeschlagen") {
    const g = passend.details?.["grund"];
    return typeof g === "string" && g.trim() ? `Der Versand an den Gast ist gescheitert: ${g}` : "Der Versand an den Gast ist gescheitert";
  }
  return p.rechnung?.modus === "vorhandene_senden" ? "Die Rechnung ist erstellt, aber noch nicht an den Gast gesendet" : "Zu dieser Zahlung gibt es noch keine Rechnung";
}
